import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// utils
import { capitalize } from '../../../utils/text';

// Appt Components
import Box from '../../../components/simple/box/Box';
import DataTable from '../../../components/simple/dataTable/DataTable.js';
import Text from '../../../components/simple/text/Text';
import Divider from '../../../components/simple/divider/Divider'

// Services
import FormService from '../../../services/FormService';
import LocalStorageService from '../../../services/LocalStorageService';
import { isServiceAdmin } from '../../../utils/permissions.js';
import { Anchor } from 'grommet';
import ActionButton from '../../../components/compound/actionButton/ActionButton.js';



const IncidentSearchResults = props => {
  const apiToken = LocalStorageService.get('apiToken');
  const activeOrg = LocalStorageService.get('activeOrg');
  const terms = LocalStorageService.get('terms')

  const [submissions, setSubmissions] = useState([]);
  const [responses, setResponses] = useState([]);

  const navigate = useNavigate();

  const deleteReport = async submissionId => {
    if (window.confirm('Are you sure you want to delete this?')) {
      await FormService.updateSubmission(apiToken, submissionId, {
        status: 'Archived',
      });
      window.location.reload(); // this is not ideal
    }
  };

  const [sort, setSort] = useState({
    property: 'reference',
    direction: 'desc'
  })

  const [showIncidents, setShowIncidents] = useState(false)

  const [loading, setLoading] = useState(false)

  const [showCSV, setShowCSV] = useState(false)


  const downloadCSV = () => {
    const csvRows = []
    const submissions = props.results || []

    let suTerm = capitalize(terms?.serviceUser || 'Service User')

    const headers = [
      'Reference',
      `${suTerm} First Name`,
      `${suTerm} Last Name`,
      'Incident Date',
      'Incident Time',
      'Report Type',
      'Year Group',
      'Staff Name',
      'Location',
      'Session/Lesson',
      'Description of incident',
      'Physical Intervention Used',
      'Restrictive Physical Intervention Used',
      'Actions Agreed',
      'Responsible for Follow Up',
      'Parents Informed',
      'Incident Link'
    ]
    csvRows.push(headers.join(','))

    submissions.forEach(submission => {

      let subType = submission.subType === 'highLevel' ? 'high-level/' : ''
      let host = 'https://www.behavioursmart.app'
      let url = `${host}/incident/${subType}${submission.id}/edit/${submission.reference}`

      let reportType = submission.subType === 'highLevel' ? 'Physical Intervention Report' : 'Incident Report'

      let row = [
        submission.reference ? `besmart-${submission.reference}` : 'n/a',
        submission.firstname || '',
        submission.lastname || '',
        moment(submission.dateOfIncident).format('DD/MM/YY') === 'Invalid date'
          ? ''
          : moment(submission.dateOfIncident).format('DD/MM/YY'),
        submission.timeOfIncident || '',
        reportType || '',
        submission.yearGroup || '',
        submission.staffName || '',
        submission.location || '',
        submission.sessionLesson || '',
        submission.incidentDescription || '',
        submission.physicalInterventionUsed || '',
        submission.restrictedPhysicalInterventionUsed || '',
        submission.actionAgreed || '',
        submission.responsibleForFollowUp || '',
        submission.parentsInformed || '',


        url
      ]

      // escape quotes by doubling them, then wrap in quotes to preserve commas and line breaks
      row = row.map(item => `"${item.replace(/"/g, '""')}"`)
      csvRows.push(row.join(','))
    })

    const csvString = csvRows.join('\n')
    const blob = new Blob([csvString], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    // window.open(url, '_blank')

    let fileLink = document.createElement('a');
        fileLink.href = url;
        fileLink.download = 'BeSmart - Incident Summary - ' + moment().format('DD-MM-YYYY') + '.csv';
        fileLink.click();

  }

  const downloadSignificantIncidentsCSV = () => {
    const csvRows = []
    const submissions = props.significantIncidents || []

    let suTerm = capitalize(terms?.serviceUser || 'Service User')

    const headers = [
      'Reference',
      `${suTerm} First Name`,
      `${suTerm} Last Name`,
      'Incident Date',
      'Incident Time',
      'Report Type',
      'Description of incident',
      'Actions Agreed',
      'Responsible for Follow Up',
      'Parents Informed'
    ]


  

    csvRows.push(headers.join(','))

    submissions.forEach(submission => {

      let subType = submission.subType === 'highLevel' ? 'high-level/' : ''
      let host = 'https://www.behavioursmart.app'
      let url = `${host}/incident/${subType}${submission.id}/edit/${submission.reference}`

      let reportType = submission.subType === 'highLevel' ? 'Physical Intervention Report' : 'Incident Report'

      let row = [
        submission.reference ? `besmart-${submission.reference}` : 'n/a',
        submission.firstname || '',
        submission.lastname || '',
        moment(submission.dateOfIncident).format('DD/MM/YY') === 'Invalid date'
          ? ''
          : moment(submission.dateOfIncident).format('DD/MM/YY'),
        submission.timeOfIncident || '',
        reportType || '',

        submission.incidentDescription || '',
        submission.actionAgreed || '',
        submission.responsibleForFollowUp || '',
        submission.parentsInformed || ''
      ]
      // escape quotes by doubling them, then wrap in quotes to preserve commas and line breaks
      row = row.map(item => `"${item.replace(/"/g, '""')}"`)
      csvRows.push(row.join(','))
    })

    const csvString = csvRows.join('\n')
    const blob = new Blob([csvString], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    // window.open(url, '_blank')

    let fileLink = document.createElement('a');
        fileLink.href = url;
        fileLink.download = 'BeSmart - Significant Incidents - ' + moment().format('DD-MM-YYYY') + '.csv';
        fileLink.click();

  }




  const resultColumns = [
    {
      property: 'reference',
      header: <Text>Incident No</Text>,
      render: datum => (
        <Anchor href={datum.pdfUrl} color='primary'>
          <Text>{datum.id ? `besmart-${datum.reference}` : ''}</Text>
        </Anchor>
      ),
    },
    {
      property: 'firstName',
      header: (
        <Text>{capitalize(terms?.serviceUser || 'Service User')}</Text>
      ),
      render: datum => (
        <Anchor href={`/serviceuser/${datum.userId}/edit`} color='primary'>
          <Text>
            {`${datum.firstname || ''} ${datum.lastname || ''}`}
          </Text>
        </Anchor>
      ),
    },
    {
      property: 'dateOfIncident',
      header: <Text>Date Of Incident</Text>,
      render: datum => (
        <Text>
          {moment(datum.dateOfIncident).format('DD/MM/YY') === 'Invalid date'
            ? 'No Date'
            : moment(datum.dateOfIncident).format('DD/MM/YY')}
        </Text>
      ),
    },
    {
      property: 'parentsInformed',
      header: <Text>Parents Informed</Text>,
      render: datum => {
        const setParentsInformed = async value => {
          await FormService.updateSubmission(apiToken, datum.id, {
            parentsInformed: value,
          });


          document.getElementById(
            `parentsinformed-${datum.id}-${value === 'no' ? 'yes' : 'no'}`,
          ).style.opacity = 0.15;

          document.getElementById(
            `parentsinformed-${datum.id}-${value}`,
          ).style.opacity = 1;
        };

        return (
          <Box direction="row" width="100%">
            <ActionButton plain onClick={() => setParentsInformed('no')}>
              <FontAwesomeIcon
                id={`parentsinformed-${datum.id}-no`}
                className={`parentsinformed-${datum.id}`}
                icon={['fa', 'thumbs-down']}
                color="#777"
                style={
                  !['yes'].includes(datum.parentsInformed)
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setParentsInformed('yes')}>
              <FontAwesomeIcon
                id={`parentsinformed-${datum.id}-yes`}
                className={`parentsinformed-${datum.id}`}
                icon={['fa', 'thumbs-up']}
                color="#00FF00"
                style={
                  'yes' === datum.parentsInformed
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
          </Box>
        );
      },
    },
    {
      property: 'flag',
      header: <Text>Flag</Text>,
      render: datum => {
        const setColour = async color => {
          await FormService.updateSubmission(apiToken, datum.id, {
            flag: color,
          });

          Array.from(
            document.getElementsByClassName(`flag-${datum.id}`),
          ).forEach(function (element, index, array) {
            element.style.opacity = 0.15;
          });

          document.getElementById(
            `flag-${datum.id}-${color}`,
          ).style.opacity = 1;
        };

        return (
          <Box direction="row" width="100%">
            <ActionButton plain onClick={() => setColour('none')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-none`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#777"
                style={
                  !['red', 'green', 'yellow'].includes(datum.flag)
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('green')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-green`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#00FF00"
                style={
                  'green' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('yellow')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-yellow`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#FFA500"
                style={
                  'yellow' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
            <ActionButton plain onClick={() => setColour('red')}>
              <FontAwesomeIcon
                id={`flag-${datum.id}-red`}
                className={`flag-${datum.id}`}
                icon={['fa', 'flag']}
                color="#FF0000"
                style={
                  'red' === datum.flag
                    ? { margin: '1px', opacity: 1 }
                    : { margin: '1px', opacity: 0.15 }
                }
              />
            </ActionButton>
          </Box>
        );
      },
    },
    {
      property: 'actions',
      header: <Text>Actions</Text>,
      render: datum => (
        <>
          <Box direction="row" width="100%">
            {props?.type !== 'smartplan' && (
              <ActionButton
                onClick={() => {

                  if (datum?.subtype === 'highlevel') {
                    navigate(`/incident/high-level/${datum.userId}/edit/${datum.id}`);
                  } else {
                    navigate(`/incident/${datum.userId}/edit/${datum.id}`);
                  }

                }}
                target="_self"
                plain
                children={
                  <Box pad="5px">
                    <FontAwesomeIcon icon={['fal', 'edit']} color="#ff8300" />
                  </Box>
                }
              />
            )}

            {datum.pdfUrl && (
              <>
                <ActionButton
                  target="_blank"
                  plain
                  href={datum.pdfUrl}
                  children={
                    <Box pad="5px">
                      <FontAwesomeIcon
                        icon={['fal', 'download']}
                        color="#ff8300"
                      />
                      {/* <Download color='primary' size='medium' disabled /> */}
                    </Box>
                  }
                />

                <ActionButton
                  plain
                  href={"/pdfviewer?url=" + encodeURIComponent(datum.pdfUrl)}
                  children={
                    <Box pad="5px">
                      <FontAwesomeIcon
                        icon={['fal', 'eye']}
                        color="#ff8300"
                      />
                      {/* <Download color='primary' size='medium' disabled /> */}
                    </Box>
                  }
                />

                {isServiceAdmin() && <ActionButton
                  target="_blank"
                  plain
                  onClick={() => {
                    deleteReport(datum.id);
                  }}
                  children={
                    <Box pad="5px">
                      <FontAwesomeIcon
                        icon={['fal', 'trash']}
                        color="#ff8300"
                      />
                    </Box>
                  }
                />}
              </>
            )}
          </Box>
        </>
      ),
    },
  ];

  return (
    <Box background='white' direction='column' gap='small' round='small' margin={{ top: 'small', bottom: 'medium'}}>
      <Box
        gap='small'
        margin={{ horizontal: 'small' }}
        pad={{ horizontal: 'xsmall', top: 'none' }}
        round='small'
      >

        <Box direction='row' justify='between' margin={{ top: 'medium', bottom: 'medium' }}>
            <Text margin={{ top: 'small', bottom: 'small' }} size='xlarge'>Additional Actions</Text>
            {!showCSV && <Box alignSelf='center' direction='row' gap='none'>
                <ActionButton size='small' onClick={() => { setShowIncidents(!showIncidents); }} label={showIncidents ? 'Hide Related Incidents' : 'Show Related Incidents'} primary />
                <ActionButton size='small' onClick={() => { setShowCSV(true); }} label='Download CSV' primary />
                {props?.additionalButtons}
            </Box>}

            {showCSV && <Box alignSelf='center' direction='row' gap='none'>
              
              <ActionButton size='small' onClick={() => { downloadCSV() }} label='Incident Summary CSV' primary />
              <ActionButton size='small' onClick={() => { downloadSignificantIncidentsCSV() }} label='Significant Incidents CSV' primary />
              <ActionButton size='small' onClick={() => { setShowCSV(false); }} label='Back' secondary />


            </Box>}

        </Box>
        {showIncidents && <>
            <Divider />
            <Box direction='column' gap='xsmall' margin={{ top: 'none' }}>
                    <Text margin={{ top: 'small', bottom: 'small' }} size='xlarge'>Related Incidents</Text>

                    <Text margin={{ top: 'small', bottom: 'small' }} size='medium'>First 250 Results ({props.results?.length || 0} found)</Text>
                    {props.results?.length > 0 &&<DataTable
                      columns={resultColumns}
                      data={props.results}
                      primaryKey="id"
                      step={250}
                      paginate={true}
                      sort={sort}
                      onSort={setSort}
                    />}
                    {!props.results?.length > 0 && <Text color='#666'>- No results to display -</Text>}

            </Box>
          </>}
        </Box>
      </Box>
  );
};

export default IncidentSearchResults;
